import React, { useEffect, useRef, useState } from 'react'
import ConfirmDialog from '../../components/dialogs/ConfirmDialog'
import ErrorDialog from '../../components/dialogs/ErrorDialog'
import GeneralModal from '../../components/dialogs/GeneralModal'
import ToastDialog, { PerminantToast } from '../../components/dialogs/toast_dialog/ToastDialog'
import LoaderComponent from '../../components/loader/LoaderComponent'
import OnScreenAssist from '../../components/ui/hint/OnScreenAssist'
import Authentication from '../../libs/api/services/Authentication'

const CommonFunctionsContext = React.createContext()

export const CommonFunctionsProvider = ({ children }) => {
    const helpRefs = useRef({})
    const [toastPerm, setToastPerm] = useState({ show: false })
    const [sectionInfo, setSectionInfo] = useState()
    const [debug, setDebug] = useState()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState({ error: undefined, onClose: () => setError({ error: undefined }), humanize: true })
    const [toast, setToast] = useState({ message: undefined, icons: undefined, time: 1 })
    const [modal, setModal] = useState([])
    const [confirmation, setConfirmation] = useState([])
    const [assistant, setAssistant] = useState({ items: [], show: false })
    const showError = (e) => setError({ ...error, error: e, humanize: true })
    const showToast = (message, icon, time) => {
        setToast({
            message: message, icon: icon, time: time, onTimeOut: () => {
                setToast({ message: undefined, icon: undefined })
            }
        })
    }

    const isNullOrEmpty = (v) => !v || v.trim().length === 0

    const asyncLoader = async (f) => {
        setLoading(true)
        await f()
        setLoading(false)
    }

    const createModal = (c, t, i, oc) => setModal([...modal, { id: modal.length, component: c, title: t, icon: i, onClose: oc }])
    const removeModal = (i) => setModal(modal.filter((x) => x.id !== i))
    // const removeModal = (i) => { }

    const GlobalRef = (e, k) => helpRefs.current[k] = { current: e ??= null }

    // console.log(e)

    const setAssistantSection = (section) => {
        setAssistant(prev => {
            if (prev.section === section) return prev;
            if (!section) return { items: [], show: false };
            return { ...prev, section };
        });
    }

    // onClose
    //ToastDialog({ key, message, icon, className, time, onTimeOut })
    return (
        <CommonFunctionsContext.Provider value={{
            showError,
            showConfirm: (c) =>
                setConfirmation({
                    ...c, open: true,
                    onCancel: () => { setConfirmation({ ...confirmation, open: false }); c.onCancel() },
                    onConfirm: () => {
                        const result = c.onConfirm()
                        if (result || typeof result === 'undefined') setConfirmation({ ...confirmation, open: false });
                    },
                }), //onCancel, onConfirm, title, heading, open, children, type
            showToast,
            createModal,
            DEBUG: setDebug,
            sectionInfo, setSectionInfo, isNullOrEmpty,
            setLoading, asyncLoader,
            setToastPerm,
            setAssistantSection,
            helpRefs,
            GlobalRef
        }}>
            {loading && <LoaderComponent />}
            <PerminantToast {...toastPerm} />
            <OnScreenAssist {...assistant} />
            <ErrorDialog {...error} />
            <ToastDialog {...toast} />
            <ConfirmDialog {...confirmation} />
            <SessionAboutToExpire />
            {modal.map((c, i) => <GeneralModal zIndex={10010 + i} key={`GenMod-${i}`} idx={c.id} onClose={
                () => {
                    if (c.onClose) c.onClose()
                    removeModal(i)
                }
            } show={true} title={c.title} icon={c.icon}>
                {{ ...c.component, props: { ...c.component.props, onClose: () => removeModal(c.id), close: f => { removeModal(c.id); f(); if (c.onClose) c.onClose() } } }}</GeneralModal>
            )}
            {debug && <div style={{ background: 'red' }}>{debug}</div>}
            {children}
        </CommonFunctionsContext.Provider>
    )
}

export default CommonFunctionsContext


const SessionAboutToExpire = () => {
    const isBusyRef = useRef(true)
    const [show, setShow] = useState(false)
    const [remaining, setRemaining] = useState(0)
    const tmrRef = useRef(undefined)
    const tmr2Ref = useRef(undefined)
    const timeoutRef = useRef(undefined)

    const setupTimers = (timeout) => {
        if (tmrRef.current) clearTimeout(tmrRef.current)
        if (tmr2Ref.current) clearTimeout(tmr2Ref.current)
        if (timeout > -1) {
            if (window.location.pathname.toLowerCase() !== '/expired' && window.location.pathname !== '/logout') {
                tmr2Ref.current = setTimeout(() => {
                    if (window.location.pathname.toLowerCase() !== '/expired' && window.location.pathname !== '/logout')
                        window.location = '/expired'
                }, new Date(timeout).getTime() - new Date().getTime())
                tmrRef.current = setTimeout(() => {
                    if (!isBusyRef.current) {
                        setShow(true)
                    }
                    isBusyRef.current = false
                }, new Date(timeout - 120000).getTime() - Date.now())
                // initialize remaining when timers are set
                setRemaining(timeout - Date.now())
            }
        }
    }

    useEffect(() => {
        const timeout = Authentication.getTimeOut();
        timeoutRef.current = timeout;
        setupTimers(timeout);
        const activityTimeoutRef = { current: undefined }
        const mouseHandler = () => {
            // Mark user as busy on activity to suppress the "about to expire" modal
            isBusyRef.current = true
            // Clear any existing activity debounce
            if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current)
            // After 5 seconds of no activity consider user not busy
            activityTimeoutRef.current = setTimeout(() => { isBusyRef.current = false }, 5000)
            // Note: do NOT clear the session timers here; we want the session timers to fire
            // even if the user was active earlier. Clearing the timers without recreating them
            // prevented the warning from ever appearing.
        };
        window.addEventListener("mousemove", mouseHandler, false);
        window.addEventListener("touchstart", mouseHandler, false);
        window.addEventListener("keydown", mouseHandler, false);
        return () => {
            if (tmrRef.current) clearTimeout(tmrRef.current)
            if (tmr2Ref.current) clearTimeout(tmr2Ref.current)
            window.removeEventListener("mousemove", mouseHandler, false);
            window.removeEventListener("touchstart", mouseHandler, false);
            window.removeEventListener("keydown", mouseHandler, false);
            if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current)
        }
    }, [])

    // Log a heartbeat immediately and every minute to confirm session logic is active
    useEffect(() => {
        const logSessionStatus = () => {
            const t = timeoutRef.current || Authentication.getTimeOut()
            const remainingMs = t - Date.now()
            const mins = Math.floor(Math.max(0, remainingMs) / 60000)
            const secs = Math.floor((Math.max(0, remainingMs) % 60000) / 1000)
            const remainingStr = `${mins}:${String(secs).padStart(2, '0')}`
            console.log('SessionHeartbeat:', {
                time: new Date().toISOString(),
                tokenPresent: !!localStorage.getItem('token'),
                timeoutValue: t,
                remainingMs: remainingMs,
                remaining: remainingStr,
                warningVisible: show
            })
        }
        // immediate log
        logSessionStatus()
        const intervalId = setInterval(logSessionStatus, 60 * 1000)
        return () => clearInterval(intervalId)
    }, [show])

    // Update remaining every second while the warning is visible
    useEffect(() => {
        let iv
        if (show) {
            iv = setInterval(() => {
                const t = timeoutRef.current || Authentication.getTimeOut()
                setRemaining(t - Date.now())
            }, 1000)
        }
        return () => { if (iv) clearInterval(iv) }
    }, [show])

    const handleRenewSession = async () => {
        setShow(false)
        await Authentication.reloadFacilities()
        const timeout = Authentication.getTimeOut();
        timeoutRef.current = timeout;
        setupTimers(timeout);
    }
    // if (!tmr) setTmr(checKWhen())
    // 176727
    // 176,727
    const formatRemaining = (ms) => {
        if (!ms || ms <= 0) return '0:00'
        const totalSeconds = Math.floor(ms / 1000)
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60
        return `${minutes}:${String(seconds).padStart(2, '0')}`
    }
    return (
        // tmrout.current < new Date().getTime() ?
        // <></>
        // :
        window.location.pathname.toLowerCase() !== '/expired' && window.location.pathname !== '/logout' &&
        <div className={`${(!show ? 'close' : 'show')} position-fixed d-flex flex-column align-items-center justify-content-center`} style={{
            zIndex: 10090, visibility: (show && window.location.pathname.toLowerCase() !== '/expired' && window.location.pathname.toLowerCase() !== '/logout') ? 'visible' : 'hidden', width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.5)'
        }}>
            <div className='text-center bg-primary-container shadow rounded p-3'>
                <p>Your session will expire in {formatRemaining(remaining)}</p>
                <button onClick={handleRenewSession} className='btn text-white'>Extend session</button>
                {/* <FontAwesomeIcon className='me-2' icon={faExclamationCircle} size={'3x'} /><div className='error-message'>{cachedError}</div> */}
                {/* <div onClick={(e) => _handlerClearError()} className='close-dialog'>
                    <FontAwesomeIcon icon={faTimesCircle} size={'2x'} />
                </div> */}
            </div>
        </div>

    )
}